// proxy.ts — ingest-time review-proxy generation (EGO-136 workstream 2). Thin
// browser seam over core/'s tested transcoder (core/src/proxy.ts): read
// left.mp4 out of a session dir handle, transcode it to a 360p faststart
// review proxy, write proxy.mp4 back. Runs automatically during Sync so a
// session arrives in R2 already carrying derived/proxy.mp4 (upload.ts's
// DERIVED_RE routes proxy.mp4 under derived/). Mirrors transcribe.ts exactly.
import { makeProxy, type ProxyStats } from "../../core/src/proxy.ts";

async function fileExists(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try { await dir.getFileHandle(name); return true; }
  catch { return false; }
}

export interface ProxyResult { skipped: boolean; stats?: ProxyStats }

/**
 * Transcode the session's left.mp4 into proxy.mp4 inside `dir`. Returns a skip
 * (never throws for the "nothing to do" cases): a proxy already present, or no
 * left.mp4 to transcode. On success the telemetry — actualBps + msPerMinute,
 * our slow-machine cost signal — is logged, not swallowed.
 */
export async function proxyInto(
  dir: FileSystemDirectoryHandle,
  opts: { log?: (m: string) => void; onProgress?: (frac: number) => void } = {},
): Promise<ProxyResult> {
  const log = opts.log ?? (() => {});

  if (await fileExists(dir, "proxy.mp4")) {
    log("  proxy.mp4 already present — skipping transcode");
    return { skipped: true };
  }

  let left: File;
  try { left = await (await dir.getFileHandle("left.mp4")).getFile(); }
  catch { log("  no left.mp4 — nothing to transcode"); return { skipped: true }; }

  const { bytes, stats } = await makeProxy(left, { onProgress: opts.onProgress });

  const fh = await dir.getFileHandle("proxy.mp4", { create: true });
  const ws = await fh.createWritable();
  await ws.write(bytes);
  await ws.close();

  log(`  proxy.mp4: ${(stats.outputBytes / 1048576).toFixed(1)} MB, ` +
      `${(stats.actualBps / 1000).toFixed(0)} kbps, ${stats.durationS.toFixed(1)}s of media ` +
      `in ${stats.seconds.toFixed(1)}s (${Math.round(stats.msPerMinute)} ms/min)`);
  return { skipped: false, stats };
}
